"use client"

import { useState, useEffect, useRef, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { User, Lock, Mail, ArrowRight, Building, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Link from "next/link";
import { motion } from "motion/react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { isValidRedirectUrl } from "@/lib/auth-utils";

const formSchema = z.object({
  accountType: z.enum(["landlord", "tenant"], {
    required_error: "Please select an account type"
  }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

type FormValues = z.infer<typeof formSchema>;

function LoginContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const searchParams = useSearchParams();
  const hasRun = useRef(false);
  
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    
    const error = searchParams.get('error');
    const role = searchParams.get('role');
    const redirect = searchParams.get('redirect');
    
    if (error) {
      const errorMessages: Record<string, string> = {
        authentication_required: "Please sign in to access this page",
        leave_review_authentication_required: "You need to be logged in to leave a review",
        profile_fetch_failed: "Unable to load your profile. Please try signing in again",
        invalid_user_role: "Your account permissions need to be updated. Please contact support",
        invalid_role: "Invalid account type. Please contact support",
        unauthorized_role_leave_review: role === 'landlord' 
          ? "Only tenants can leave reviews. Landlords cannot access the review system"
          : "You don't have permission to leave reviews",
        auth_callback_failed: "Authentication process failed. Please try again",
        session_exchange_failed: "Session setup failed. Please try signing in again", 
        user_verification_failed: "Account verification failed. Please try again",
        auth_error: "An authentication error occurred. Please try again",
        client_auth_failed: "Session expired. Please sign in again",
        logout_failed: "There was an issue signing you out. Please try again"
      };
      
      const message = errorMessages[error] || "Authentication required. Please sign in to continue";
      toast.error("Access Denied", {
        description: message,
        duration: 6000,
      });
    }
    
    if (redirect && typeof window !== 'undefined' && isValidRedirectUrl(redirect)) {
      sessionStorage.setItem('rentwise-login-redirect', redirect);
    }
  }, [searchParams]);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountType: "tenant",
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('accountType', data.accountType);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          const resetTime = result.resetTime ? ` Try again in ${result.resetTime} minutes.` : '';
          toast.error("Too many login attempts", {
            description: `${result.message}${resetTime}`,
            duration: 8000,
          });
        } else {
          toast.error(result.message || "Login failed");
        }
        setIsLoading(false);
        return;
      }
      
      toast.success("Successfully signed in!");
      
      const storedRedirect = typeof window !== 'undefined' 
        ? sessionStorage.getItem('rentwise-login-redirect') 
        : null;
      
      if (storedRedirect && isValidRedirectUrl(storedRedirect)) {
        sessionStorage.removeItem('rentwise-login-redirect');
        window.location.href = storedRedirect;
      } else {
        if (storedRedirect) sessionStorage.removeItem('rentwise-login-redirect');
        window.location.href = result.redirectUrl || '/dashboard';
      }
      
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100vh] min-h-[100dvh] flex flex-col">
      <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
      
      <main className="flex-1 flex flex-col md:flex-row">
        {/* Left side - login form */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-16 bg-gradient-to-b from-[var(--color-neutral-50)] via-[var(--color-pure-white)] to-[var(--color-hover-overlay)]"
        >
          <div className="w-full max-w-md space-y-8">
            <div className="text-center md:text-left space-y-3">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-brand-primary-hover)] bg-clip-text text-transparent">
                Welcome back
              </h1>
              <p className="text-[var(--color-neutral-600)]">Sign in to your RentWise account</p>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[var(--color-neutral-700)]">Account Type</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] transition-colors">
                            <SelectValue placeholder="Select an account type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-[var(--color-pure-white)] border border-[var(--color-neutral-300)] shadow-lg z-50 backdrop-blur-sm">
                          <SelectItem value="tenant" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2 text-[var(--color-tenant-primary)]" />
                              <span className="font-medium">Tenant</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="landlord" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center">
                              <Building className="h-4 w-4 mr-2 text-[var(--color-landlord-primary)]" />
                              <span className="font-medium">Landlord</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[var(--color-neutral-700)]">Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                          <Input 
                            placeholder="name@example.com" 
                            {...field} 
                            className="pl-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                            disabled={isLoading}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-[var(--color-neutral-700)]">Password</FormLabel>
                        <Link 
                          href="/forgot-password" 
                          className="text-sm font-medium text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary-hover)] transition-colors"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                          <Input 
                            type={showPassword ? "text" : "password"} 
                            placeholder="Enter your password" 
                            {...field} 
                            className="pl-10 pr-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                            disabled={isLoading}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-[var(--color-neutral-500)]" />
                            ) : (
                              <Eye className="h-4 w-4 text-[var(--color-neutral-500)]" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white transition-all duration-200 transform-gpu hover:scale-[1.02] active:scale-[0.98] h-12 text-base font-medium"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      Sign in
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </div>
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center">
              <p className="text-[var(--color-neutral-600)]">
                Don't have an account?{" "}
                <Link 
                  href="/register" 
                  className="font-medium text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary-hover)] transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right side - hero content */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="w-full md:w-1/2 bg-linear-to-br from-blue-600 via-blue-700 to-indigo-800 flex flex-col justify-center items-center p-8 md:p-16 text-white relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white"></div>
            <div className="absolute top-40 right-16 w-12 h-12 rounded-full bg-white"></div>
            <div className="absolute bottom-20 left-20 w-16 h-16 rounded-full bg-white"></div>
            <div className="absolute bottom-40 right-10 w-8 h-8 rounded-full bg-white"></div>
          </div>
          
          <div className="relative z-10 text-center max-w-md space-y-6">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Find Your Perfect Home
              </h2>
              <p className="text-white/80 text-lg leading-relaxed">
                Join thousands of Nigerians who trust RentWise to find safe, verified properties and connect with reliable landlords.
              </p>
            </motion.div>
            
            <motion.div 
              className="flex justify-center space-x-8 pt-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold">10,000+</div>
                <div className="text-white/70 text-sm">Verified Properties</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">50,000+</div>
                <div className="text-white/70 text-sm">Happy Tenants</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">5,000+</div>
                <div className="text-white/70 text-sm">Trusted Landlords</div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </main>
      
      <Footer />
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginContent />
    </Suspense>
  );
} 